import axios, { AxiosResponse, ResponseType } from 'axios';
import Toastify from './Toastify';
// import { PAGE_ROUTES } from '../helpers/globalEnums';

export type AllTypes = string | number | boolean | null | undefined | JsonType | JsonArray;

export interface JsonType {
    [key: string]: AllTypes;
    [key: number]: AllTypes;
}

export interface JsonArray extends Array<AllTypes> {}

export const isNullOrUndefined = (param: any) => {
    return param === null || param === undefined;
};

export const CheckNever = (p: never, throwError = true) => {
    if (throwError) {
        throw new Error(`Check Never Happened`);
    } else {
        return null;
    }
};

const MakeAxiosRequestInternal = async <RetType = any>(
    method: 'GET' | 'POST',
    url: string,
    postBodyOrQueryParams: JsonType | FormData = {}, // Allow FormData
    headers: JsonType | undefined = undefined,
    timeout = 60_000,
    maxRedirects: number | undefined = undefined,
    httpAgent: any = null,
    httpsAgent: any = null,
    responseType: ResponseType | null = null,
): Promise<AxiosResponse<RetType> | null | undefined> => {
    try {
        const config: JsonType = { timeout };
        if (responseType) {
            config['responseType'] = responseType;
        }
        if (headers) {
            config['headers'] = headers;
        }
        if (!isNullOrUndefined(maxRedirects)) {
            config['maxRedirects'] = maxRedirects;
        }
        if (httpAgent) {
            config['httpAgent'] = httpAgent;
        }
        if (httpsAgent) {
            config['httpsAgent'] = httpsAgent;
        }
        config['headers'] = config['headers'] ?? {};
        (config['headers'] as JsonType)['console-app'] = true;

        if (method === 'GET') {
            const response = await axios.get<RetType>(url, {
                ...config,
                params: postBodyOrQueryParams,
                withCredentials: true,
            });
            return response;
        } else if (method === 'POST') {
            const response = await axios.post<RetType>(
                url,
                postBodyOrQueryParams, // FormData is now allowed
                { ...config, withCredentials: true },
            );
            return response;
        }
        CheckNever(method);
    } catch (e: any) {
        return e?.response ?? null;
    }
};

export const MakeAxiosRequest = async <RetType = any>(
    method: 'GET' | 'POST',
    url: string,
    postBodyOrQueryParams: JsonType | FormData = {}, // Allow FormData
    headers: JsonType | undefined = undefined,
    timeout = 60_000,
    maxRedirects: number | undefined = undefined,
    httpAgent: any = null,
    httpsAgent: any = null,
    responseType: ResponseType | null = null,
): Promise<AxiosResponse<RetType> | null | undefined> => {
    const response = await MakeAxiosRequestInternal(method, url, postBodyOrQueryParams, headers, timeout, 0, httpAgent, httpsAgent, responseType);

    if (response?.status === 467) {
        if (response?.data?.error) {
            Toastify.error(response?.data?.error);
        } else {
            Toastify.error(`DevError: Response Status is ${response?.status}, but no error msg`);
        }
        return null; // error has been handled
    }
    if (response?.status === 462) {
        alert(`Unhandled Logout Error`);
    }
    if (response?.status === 463) {
        if (response?.data?.error) {
            window.location.href = response?.data?.error ?? '/login';
        } else {
            Toastify.show(`DevError: Response Status is ${response?.status}, but no error msg`);
        }
        return null; // error has been handled
    }
    return response;
};

export const sleep = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

export const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    try {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return 'N/A';
    }
};
